import { useCallback, useState } from "react";
import {
  RiCheckLine,
  RiExternalLinkLine,
  RiFileCopyLine,
  RiInformationLine,
} from "@remixicon/react";
import { Button } from "@/components/button";
import { Card, CardContent } from "@/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { cn } from "@/utils/utils";
import { MANUAL_DOCS_URL } from "@/components/silo/setup-node-types";

type CodeBlockProps = {
  id: string;
  code: string;
  copiedId: string | null;
  onCopy: (id: string, code: string) => void;
};

function CodeBlock({ id, code, copiedId, onCopy }: CodeBlockProps) {
  const copied = copiedId === id;
  return (
    <div className="relative group mt-3">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onCopy(id, code)}
        className="absolute top-2 right-2 z-10 bg-background/90 hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
      >
        {copied ? <RiCheckLine className="size-3.5" /> : <RiFileCopyLine className="size-3.5" />}
        {copied ? "Copied" : "Copy"}
      </Button>
      <pre className="overflow-x-auto rounded-md border border-border bg-zinc-950/80 p-4 pr-24 text-xs leading-relaxed text-zinc-100 font-mono">
        {code}
      </pre>
    </div>
  );
}

const STEPS = [
  {
    title: "Understand prerequisites",
    body: [
      "Minimum 500 GB free storage and about 2 TB bandwidth per month.",
      "Stable internet connection on Linux or Windows with Docker support.",
      "Public IP address or a properly configured Dynamic DNS name.",
      "A Larissa Network wallet address for Silo payouts.",
    ],
  },
  {
    title: "Get an authorization token",
    body: [
      "Open the Silo registration page on the Silo website.",
      "Enter your email to generate an authorization token.",
      "Save the token securely. You will use it to authorize your TrustKey.",
    ],
    link: {
      href: "https://silos3.com/signup",
      label: "Open Silo signup",
    },
  },
  {
    title: "Setup port forwarding",
    body: [
      "Open your router configuration page (often http://192.168.1.1).",
      "Forward TCP port 28967 to the machine hosting the node.",
      "Also forward UDP port 28967 if you enable QUIC.",
    ],
  },
  {
    title: "Configure QUIC",
    body: [
      "Forward UDP port 28967 as above.",
      "Confirm with your ISP that UDP traffic is not blocked.",
      "No extra node config is required for QUIC once the port is open.",
    ],
  },
] as const;

const TRUSTKEY_WINDOWS = `[Net.ServicePointManager]::SecurityProtocol = [Net.SecurityProtocolType]::Tls12; curl https://github.com/silos3/silo-release/releases/download/v1.0.0-P/trustkey_windows_amd64.exe.zip -o trustkey_windows_amd64.exe.zip; Expand-Archive ./trustkey_windows_amd64.exe.zip . -Force; Rename-Item -Path ./trustkey_windows_amd64.exe -NewName trustkey.exe`;

const TRUSTKEY_LINUX = `curl -L https://github.com/silos3/silo-release/releases/download/v1.0.0-P/trustkey_linux_amd64.zip -o trustkey_linux_amd64.zip
unzip -o trustkey_linux_amd64.zip
chmod +x trustkey_linux_amd64
sudo mv trustkey_linux_amd64 /usr/local/bin/trustkey`;

const SETUP_WINDOWS = `docker run --rm -e SETUP="true" --mount type=bind,source="<trustkey-dir>",destination=/app/trustkey --mount type=bind,source="<storage-dir>",destination=/app/config --name storagenode silos3/silo-node:latest`;

const SETUP_LINUX = `docker run --rm -e SETUP="true" \\
    --user $(id -u):$(id -g) \\
    --mount type=bind,source="<trustkey-dir>",destination=/app/trustkey \\
    --mount type=bind,source="<storage-dir>",destination=/app/config \\
    --name storagenode silos3/silo-node:latest`;

const RUN_WINDOWS = `docker run -d --restart unless-stopped --stop-timeout 300 -p 28967:28967/tcp -p 28967:28967/udp -p 127.0.0.1:14002:14002 -e WALLET="0xXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX" -e EMAIL="username@domain.com" -e ADDRESS="domain.ddns.net:28967" -e STORAGE="2TB" --mount type=bind,source="<trustkey-dir>",destination=/app/trustkey --mount type=bind,source="<storage-dir>",destination=/app/config --name storagenode silos3/silo-node:latest`;

const RUN_LINUX = `docker run -d --restart unless-stopped --stop-timeout 300 \\
  -p 28967:28967/tcp \\
  -p 28967:28967/udp \\
  -p 127.0.0.1:14002:14002 \\
  -e WALLET="0xXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX" \\
  -e EMAIL="username@domain.com" \\
  -e ADDRESS="domain.ddns.net:28967" \\
  -e STORAGE="2TB" \\
  --user $(id -u):$(id -g) \\
  --mount type=bind,source="<trustkey-dir>",destination=/app/trustkey \\
  --mount type=bind,source="<storage-dir>",destination=/app/config \\
  --name storagenode silos3/silo-node:latest`;

export function SetupNodeManual() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [openStep, setOpenStep] = useState(0);

  const copy = useCallback(async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((v) => (v === id ? null : v)), 2000);
    } catch {
      setCopiedId(null);
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="max-w-2xl">
          <h2 className="text-xl font-semibold">Manual Docker install</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Follow the official step-by-step guide. Commands below match the Silo docs for{" "}
            <span className="font-mono text-foreground">silos3/silo-node</span>.
          </p>
        </div>
        <Button asChild variant="outline" className="hover:border-[#FF8A3C] hover:text-[#FF8A3C] shrink-0">
          <a href={MANUAL_DOCS_URL} target="_blank" rel="noopener noreferrer">
            Full documentation
            <RiExternalLinkLine className="size-4" />
          </a>
        </Button>
      </div>

      <div className="rounded-md border border-[#FF8A3C]/30 bg-[#FF8A3C]/10 p-4 flex gap-3">
        <RiInformationLine className="size-5 text-[#FF8A3C] shrink-0 mt-0.5" />
        <p className="text-sm text-[#FFD3B3]">
          Prefer the guided flow? Switch to Automatic mode for a generated{" "}
          <span className="font-mono">silo-node-ui</span> compose command with OS, arch, and storage filled in.
        </p>
      </div>

      <div className="space-y-3">
        {STEPS.map((step, index) => {
          const open = openStep === index;
          return (
            <Card
              key={step.title}
              className={cn(
                "bg-sidebar border-border rounded-md overflow-hidden transition-colors",
                open && "border-[#FF8A3C]/50",
              )}
            >
              <button
                type="button"
                className="w-full text-left px-5 py-4 flex items-center gap-3"
                onClick={() => setOpenStep(open ? -1 : index)}
                aria-expanded={open}
              >
                <span className="inline-flex size-7 items-center justify-center rounded-full border border-[#FF8A3C]/50 text-xs text-[#FF8A3C] font-medium">
                  {index + 1}
                </span>
                <span className="font-medium">{step.title}</span>
              </button>
              {open && (
                <CardContent className="px-5 pb-5 pt-0 space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-10">
                    {step.body.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  {"link" in step && step.link && (
                    <div className="pl-10">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]"
                      >
                        <a href={step.link.href} target="_blank" rel="noopener noreferrer">
                          {step.link.label}
                          <RiExternalLinkLine className="size-4" />
                        </a>
                      </Button>
                      <p className="mt-2 text-xs text-muted-foreground font-mono">
                        {step.link.href}
                      </p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {/* Step 5 TrustKey */}
        <Card
          className={cn(
            "bg-sidebar border-border rounded-md overflow-hidden",
            openStep === 4 && "border-[#FF8A3C]/50",
          )}
        >
          <button
            type="button"
            className="w-full text-left px-5 py-4 flex items-center gap-3"
            onClick={() => setOpenStep(openStep === 4 ? -1 : 4)}
            aria-expanded={openStep === 4}
          >
            <span className="inline-flex size-7 items-center justify-center rounded-full border border-[#FF8A3C]/50 text-xs text-[#FF8A3C] font-medium">
              5
            </span>
            <span className="font-medium">Create a TrustKey</span>
          </button>
          {openStep === 4 && (
            <CardContent className="px-5 pb-5 pt-0 space-y-4">
              <Tabs defaultValue="linux">
                <TabsList>
                  <TabsTrigger value="linux">Linux</TabsTrigger>
                  <TabsTrigger value="windows">Windows</TabsTrigger>
                </TabsList>
                <TabsContent value="linux" className="mt-4 space-y-4">
                  <p className="text-sm text-muted-foreground">Download and install the TrustKey tool:</p>
                  <CodeBlock id="tk-linux" code={TRUSTKEY_LINUX} copiedId={copiedId} onCopy={copy} />
                  <p className="text-sm text-muted-foreground">Generate an identity:</p>
                  <CodeBlock
                    id="tk-linux-create"
                    code="trustkey create silonode"
                    copiedId={copiedId}
                    onCopy={copy}
                  />
                  <p className="text-sm text-muted-foreground">Authorize with your token:</p>
                  <CodeBlock
                    id="tk-linux-auth"
                    code="trustkey authorize silonode <email:your-authorization-token>"
                    copiedId={copiedId}
                    onCopy={copy}
                  />
                </TabsContent>
                <TabsContent value="windows" className="mt-4 space-y-4">
                  <p className="text-sm text-muted-foreground">PowerShell download:</p>
                  <CodeBlock id="tk-win" code={TRUSTKEY_WINDOWS} copiedId={copiedId} onCopy={copy} />
                  <p className="text-sm text-muted-foreground">Generate an TrustKey:</p>
                  <CodeBlock
                    id="tk-win-create"
                    code="./trustkey.exe create silonode"
                    copiedId={copiedId}
                    onCopy={copy}
                  />
                  <p className="text-sm text-muted-foreground">Authorize the TrustKey:</p>
                  <CodeBlock
                    id="tk-win-auth"
                    code="./trustkey.exe authorize silonode <email:your-authorization-token>"
                    copiedId={copiedId}
                    onCopy={copy}
                  />
                </TabsContent>
              </Tabs>
              <p className="text-xs text-muted-foreground">
                Store TrustKey files in a secure, accessible location before continuing.
              </p>
            </CardContent>
          )}
        </Card>

        {/* Step 6 Install */}
        <Card
          className={cn(
            "bg-sidebar border-border rounded-md overflow-hidden",
            openStep === 5 && "border-[#FF8A3C]/50",
          )}
        >
          <button
            type="button"
            className="w-full text-left px-5 py-4 flex items-center gap-3"
            onClick={() => setOpenStep(openStep === 5 ? -1 : 5)}
            aria-expanded={openStep === 5}
          >
            <span className="inline-flex size-7 items-center justify-center rounded-full border border-[#FF8A3C]/50 text-xs text-[#FF8A3C] font-medium">
              6
            </span>
            <span className="font-medium">Install Silo node with Docker</span>
          </button>
          {openStep === 5 && (
            <CardContent className="px-5 pb-5 pt-0 space-y-4">
              <p className="text-sm text-muted-foreground">Pull the official image:</p>
              <CodeBlock
                id="docker-pull"
                code="docker pull silos3/silo-node:latest"
                copiedId={copiedId}
                onCopy={copy}
              />

              <Tabs defaultValue="linux">
                <TabsList>
                  <TabsTrigger value="linux">Linux</TabsTrigger>
                  <TabsTrigger value="windows">Windows</TabsTrigger>
                </TabsList>
                <TabsContent value="linux" className="mt-4 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    One-time setup. Replace <span className="font-mono">&lt;trustkey-dir&gt;</span> and{" "}
                    <span className="font-mono">&lt;storage-dir&gt;</span>:
                  </p>
                  <CodeBlock id="setup-linux" code={SETUP_LINUX} copiedId={copiedId} onCopy={copy} />
                  <p className="text-sm text-muted-foreground">
                    Run the node. Set WALLET, EMAIL, ADDRESS, and STORAGE to your values:
                  </p>
                  <CodeBlock id="run-linux" code={RUN_LINUX} copiedId={copiedId} onCopy={copy} />
                </TabsContent>
                <TabsContent value="windows" className="mt-4 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Paste into a plain text editor first, then run. Replace trustkey and storage paths:
                  </p>
                  <CodeBlock id="setup-win" code={SETUP_WINDOWS} copiedId={copiedId} onCopy={copy} />
                  <p className="text-sm text-muted-foreground">
                    Run the node after editing WALLET, EMAIL, ADDRESS, and STORAGE:
                  </p>
                  <CodeBlock id="run-win" code={RUN_WINDOWS} copiedId={copiedId} onCopy={copy} />
                </TabsContent>
              </Tabs>

              <div className="rounded-md border border-border bg-background/30 p-4 space-y-3">
                <p className="text-sm font-medium">Verify and monitor</p>
                <CodeBlock id="docker-ps" code="docker ps -a" copiedId={copiedId} onCopy={copy} />
                <p className="text-sm text-muted-foreground">
                  Node stats:{" "}
                  <a
                    href="http://127.0.0.1:14002"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FF8A3C] hover:underline"
                  >
                    http://127.0.0.1:14002
                  </a>
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
